import { useState } from 'react';
import { useClerk, useOrganization, useUser } from '@clerk/clerk-react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuItem, Tooltip } from '@strivelabs/ui-core/v2';
import { useSidebar } from '@strivelabs/ui-blocks/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { usePostHog } from 'posthog-js/react';
import { APP_ROUTES } from '@/constants';
import { useIsStrivelabsUser } from '@/hooks/useIsStrivelabsUser';
import { useUserRole } from '@/hooks/useUserRole';
import { ANALYTICS_EVENTS, resetAnalytics, useTrack } from '@/lib/analytics';
import { AppsIcon, LogoutIcon, ProfileIcon, SettingsIcon, UpgradeIcon } from '../icons/SidebarIcons';
import { AddAccountModal } from '@/components/auth/AddAccountModal';
import { OrgSubmenu } from './OrgSubmenu';

/** Initials fallback when the user has no avatar. */
function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
}

const AVATAR_BG = '#9A7BA8';

const ROW_CLASS =
  'flex w-full items-center gap-[7px] rounded-v2-lg py-[5px] pl-1 pr-[7px] text-[13px] leading-5 tracking-[-0.24px]';

/**
 * The account row at the foot of the rail (Home Shell design): a 24px avatar
 * wearing the organisation's mark as a corner badge, the user's name with the
 * organisation beside it, and a caret. It opens the account panel — identity
 * header, the organisation switcher, then Profile / Settings / Apps, with
 * Log out under a rule. Collapses to the badged avatar alone in the rail.
 */
export function AccountMenu() {
  const [open, setOpen] = useState(false);
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const { collapsed } = useSidebar();
  const { isGuest } = useUserRole();
  const { isStrivelabsUser } = useIsStrivelabsUser();
  const { user, isLoaded } = useUser();
  const { organization } = useOrganization();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const trackEvent = useTrack();

  const handleSignOut = () => {
    trackEvent(ANALYTICS_EVENTS.USER_SIGNED_OUT, {});
    // Reset after the capture so the event still lands on the outgoing
    // person, and before sign-out so the next user on this browser starts
    // clean instead of inheriting the previous identity.
    resetAnalytics(posthog);
    return signOut({ redirectUrl: APP_ROUTES.SIGN_IN });
  };

  // Until Clerk resolves there is no user to name; a placeholder card with
  // invented initials would look identical to an expired session.
  if (!isLoaded) {
    return (
      <div
        aria-hidden
        className={cn(
          'animate-pulse bg-v2-sidebar-accent motion-reduce:animate-none',
          collapsed ? 'mx-auto size-8 rounded-v2-md' : 'h-8 w-full rounded-v2-md',
        )}
      />
    );
  }

  const name = user?.fullName || user?.primaryEmailAddress?.emailAddress || 'Account';
  const email = user?.primaryEmailAddress?.emailAddress;
  const orgName = organization?.name;
  const orgInitial = orgName?.trim().charAt(0).toUpperCase();

  const avatar = (size: 'row' | 'panel') => (
    <span className="relative shrink-0">
      {user?.imageUrl ? (
        <img
          src={user.imageUrl}
          alt=""
          className={cn('object-cover', size === 'row' ? 'size-6 rounded-v2-md' : 'size-5 rounded-v2-md')}
        />
      ) : (
        <span
          aria-hidden
          style={{ background: AVATAR_BG }}
          className={cn(
            'grid place-items-center font-medium text-white',
            size === 'row' ? 'size-6 rounded-v2-md text-[10px]' : 'size-5 rounded-v2-md text-[9px]',
          )}
        >
          {initialsOf(name)}
        </span>
      )}
      {size === 'row' && (orgName || organization?.imageUrl) ? (
        // The organisation as a corner badge, so the one row says who and where.
        <span
          aria-hidden
          className="absolute -bottom-[2.5px] -right-[2.5px] grid size-3 place-items-center overflow-hidden rounded-full bg-v2-sidebar-active text-[7px] font-semibold text-v2-sidebar-foreground ring-[1.5px] ring-white"
        >
          {organization?.imageUrl ? (
            <img src={organization.imageUrl} alt="" className="size-3 object-cover" />
          ) : (
            orgInitial
          )}
        </span>
      ) : null}
    </span>
  );

  const trigger = (
    <button
      type="button"
      aria-label={`Account: ${name}`}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        'group/account flex h-8 items-center gap-0.5 rounded-v2-md text-v2-sidebar-foreground transition-colors duration-v2-quick',
        'hover:bg-v2-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring',
        collapsed ? 'mx-auto w-8 justify-center' : 'w-full pl-1',
        open && 'bg-v2-sidebar-accent',
      )}
    >
      {avatar('row')}
      {collapsed ? null : (
        <>
          <span className="ml-[7px] flex min-w-0 flex-1 items-baseline text-left text-[13px] leading-5 tracking-[-0.24px]">
            <span className="truncate">{name}</span>
            {orgName ? (
              <>
                <span aria-hidden className="mx-[5px] text-v2-sidebar-foreground/[0.34]">·</span>
                <span className="truncate text-[11px] tracking-[-0.08px] text-v2-sidebar-foreground/[0.55]">{orgName}</span>
              </>
            ) : null}
          </span>
          <span className="mr-1 grid h-8 w-5 shrink-0 place-items-center text-v2-sidebar-foreground/[0.45]">
            <ChevronDown size={10} strokeWidth={2.5} aria-hidden />
          </span>
        </>
      )}
    </button>
  );

  const go = (path: string) => () => navigate(path);

  return (
    // `Menu`, not `Popover`: the rail's narrow box and stacking context would
    // swallow an absolutely-positioned child; `Menu` portals to the body and
    // opens above the trigger, as the design's fixed panel does.
    <>
      <Menu
        open={open}
        onOpenChange={setOpen}
        side={collapsed ? 'right' : 'top'}
        align={collapsed ? 'end' : 'start'}
        aria-label="Account menu"
        contentClassName={cn(
          'w-[216px]',
          'p-0 shadow-[0_1px_2px_rgba(0,0,0,0.05),0_8px_28px_rgba(24,24,24,0.10)]',
        )}
        trigger={collapsed ? <Tooltip label={name} side="right">{trigger}</Tooltip> : trigger}
      >
      {/* Identity header repeats the avatar so the panel stands on its own once
          it has left the trigger — in the rail the trigger is only an avatar. */}
      <div className="flex items-start gap-[9px] border-b border-[#EDEEF0] bg-v2-sidebar-foreground/[0.025] px-3 py-[11px]">
        {avatar('panel')}
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-semibold leading-[18px] tracking-[-0.24px] text-v2-popover-foreground">{name}</span>
          {email ? (
            <span className="block truncate text-[11px] leading-[15px] tracking-[-0.08px] text-v2-sidebar-foreground/[0.62]">{email}</span>
          ) : null}
        </span>
      </div>

      {/* Guests still get the switcher: a guest invited to several
          organisations (or a guest in one and a member of another) has no
          other way to move between them. They join as `org:member`, so the
          admin-only "Add organisation" never shows for them. */}
      <OrgSubmenu onAddOrganisation={() => setIsAddAccountOpen(true)} />

      {/* Guests may not visit settings; their nav is Reports alone, so Log out
          is all the rest of this panel has to offer them. */}

      <div className="px-1.5 py-2">
        {isGuest ? null : (
          <>
            {/* Plans live behind the StriveLabs-only billing routes for now. */}
            {isStrivelabsUser ? (
              <MenuItem label="Upgrade Plan" className={ROW_CLASS} onClick={go('/ui/settings/billing/plan')}>
                <span className="grid size-3.5 shrink-0 place-items-center text-[15px]"><UpgradeIcon /></span>
                Upgrade Plan
              </MenuItem>
            ) : null}
            <MenuItem label="Profile" className={ROW_CLASS} onClick={go('/ui/settings/profile')}>
              <span className="grid size-3.5 shrink-0 place-items-center text-[15px]"><ProfileIcon /></span>
              Profile
            </MenuItem>
            <MenuItem label="Settings" className={ROW_CLASS} onClick={go('/ui/settings')}>
              <span className="grid size-3.5 shrink-0 place-items-center text-[15px]"><SettingsIcon /></span>
              Settings
            </MenuItem>
            <MenuItem label="Get Apps & Extensions" className={ROW_CLASS} onClick={go('/ui/settings/connections')}>
              <span className="grid size-3.5 shrink-0 place-items-center text-[15px]"><AppsIcon /></span>
              Get Apps & Extensions
            </MenuItem>
            <span aria-hidden className="my-[7px] -mx-1.5 block h-px bg-[#EDEEF0]" />
          </>
        )}
        <MenuItem label="Log out" destructive className={ROW_CLASS} onClick={handleSignOut}>
          <span className="grid size-3.5 shrink-0 place-items-center text-[15px]"><LogoutIcon /></span>
          Log out
        </MenuItem>
      </div>
      </Menu>
      <AddAccountModal open={isAddAccountOpen} onOpenChange={setIsAddAccountOpen} />
    </>
  );
}
